import { useState, useEffect, useRef, useCallback } from "react";
import { Mic, MicOff } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * SpeechToTextButton - A reusable component for voice input
 * Uses the Web Speech API for browser-based speech recognition
 * Works on Chrome, Edge, Safari (desktop and mobile)
 */
export const SpeechToTextButton = ({ 
  onTranscript, 
  appendMode = true,
  className = "",
  size = "icon",
  disabled = false
}) => {
  const [isListening, setIsListening] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [transcript, setTranscript] = useState('');
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const restartTimeoutRef = useRef(null);
  const finalTranscriptRef = useRef('');
  const isStoppedManuallyRef = useRef(false);

  // Check for browser support on mount
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setIsSupported(false);
      console.log('Speech recognition not supported in this browser');
    }
    
    return () => {
      stopListening();
    };
  }, []);

  const stopListening = useCallback(() => {
    isStoppedManuallyRef.current = true;
    
    if (recognitionRef.current) {
      try { 
        recognitionRef.current.stop(); 
      } catch(e) {
        console.log('Error stopping recognition:', e);
      }
    }
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    if (restartTimeoutRef.current) {
      clearTimeout(restartTimeoutRef.current);
      restartTimeoutRef.current = null;
    }
    
    // Send final transcript if we have one
    if (finalTranscriptRef.current.trim()) {
      const cleanedText = cleanDuplicates(finalTranscriptRef.current.trim());
      onTranscript(cleanedText, appendMode);
      toast.success("Got it!", { duration: 1500 });
    }
    
    finalTranscriptRef.current = '';
    setTranscript('');
    setIsListening(false);
  }, [onTranscript, appendMode]);

  const startListening = useCallback(async () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      toast.error("Speech recognition not supported. Try Chrome, Edge, or Safari.");
      return;
    }

    // Request microphone permission first
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Stop the stream immediately - we just needed permission
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      console.error('Microphone permission error:', err);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        toast.error("Microphone access denied. Please allow microphone access and try again.");
      } else if (err.name === 'NotFoundError') {
        toast.error("No microphone found. Please connect a microphone.");
      } else {
        toast.error("Could not access microphone. Please check your settings.");
      }
      return;
    }

    // Reset state
    isStoppedManuallyRef.current = false;
    finalTranscriptRef.current = '';
    setTranscript('');
    
    try {
      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;
      
      // Configuration for better results
      recognition.continuous = true;
      recognition.interimResults = true; // Enable interim for better feedback
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;

      recognition.onstart = () => {
        console.log('Speech recognition started');
        setIsListening(true);
        toast.info("🎤 Listening... Speak now!", { duration: 2000 });
      };

      recognition.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          const text = result[0].transcript;
          
          if (result.isFinal) {
            finalTranscript += text + ' ';
          } else {
            interimTranscript += text;
          }
        }
        
        // Add to our accumulated final transcript
        if (finalTranscript) {
          finalTranscriptRef.current += finalTranscript;
        }
        
        // Update display (for potential visual feedback)
        setTranscript(finalTranscriptRef.current + interimTranscript);
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        
        switch (event.error) {
          case 'no-speech':
            // This is common - don't stop, just keep listening
            console.log('No speech detected, continuing...');
            return;
          case 'audio-capture':
            toast.error("Microphone error. Please check your microphone.");
            break;
          case 'not-allowed':
            toast.error("Microphone access denied. Please allow access in browser settings.");
            break;
          case 'network':
            toast.error("Network error. Please check your connection.");
            break;
          case 'aborted':
            // User stopped, don't show error
            return;
          default:
            // Don't show error for minor issues
            console.log('Recognition error:', event.error);
            return;
        }
        
        setIsListening(false);
      };

      recognition.onend = () => {
        console.log('Speech recognition ended, manual stop:', isStoppedManuallyRef.current);
        
        // If not manually stopped, restart to keep listening
        if (!isStoppedManuallyRef.current && isListening) {
          console.log('Auto-restarting recognition...');
          restartTimeoutRef.current = setTimeout(() => {
            if (!isStoppedManuallyRef.current && recognitionRef.current) {
              try {
                recognitionRef.current.start();
              } catch (e) {
                console.log('Could not restart:', e);
                setIsListening(false);
              }
            }
          }, 100);
        } else {
          setIsListening(false);
        }
      };

      // Start recognition
      recognition.start();
      
      // Maximum recording time - 3 minutes
      timeoutRef.current = setTimeout(() => {
        toast.info("Recording stopped (3 minute limit)");
        stopListening();
      }, 180000);
      
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      toast.error("Failed to start voice input. Please try again.");
      setIsListening(false);
    }
  }, [isListening, stopListening]);

  // Clean up duplicate words/phrases (common on mobile)
  const cleanDuplicates = (text) => {
    if (!text || text.length < 10) return text;
    
    const words = text.split(/\s+/);
    const result = [];
    let i = 0;
    
    while (i < words.length) {
      // Check for immediate word duplicates
      if (i > 0 && words[i].toLowerCase() === words[i-1]?.toLowerCase()) {
        i++;
        continue;
      }
      
      // Check for phrase duplicates (2-5 words)
      let foundDupe = false;
      for (let len = 2; len <= 5 && i + len * 2 <= words.length; len++) {
        const phrase1 = words.slice(i, i + len).join(' ').toLowerCase();
        const phrase2 = words.slice(i + len, i + len * 2).join(' ').toLowerCase();
        
        if (phrase1 === phrase2) {
          result.push(...words.slice(i, i + len));
          i += len * 2;
          foundDupe = true;
          break;
        }
      }
      
      if (!foundDupe) {
        result.push(words[i]);
        i++;
      }
    }
    
    return result.join(' ');
  };

  const handleClick = () => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  // Don't render if not supported
  if (!isSupported) {
    return null;
  }

  return (
    <Button
      type="button"
      variant={isListening ? "destructive" : "outline"}
      size={size}
      onClick={handleClick}
      disabled={disabled}
      className={`transition-all duration-200 ${isListening ? 'animate-pulse ring-2 ring-red-400 bg-red-500 hover:bg-red-600' : 'hover:bg-slate-700'} ${className}`}
      title={isListening ? "Click to stop recording" : "Click to speak"}
      data-testid="speech-to-text-btn"
    >
      {isListening ? (
        <MicOff className="w-4 h-4 text-white" />
      ) : (
        <Mic className="w-4 h-4" />
      )}
    </Button>
  );
};

export default SpeechToTextButton;
